import { useCallback, useEffect } from "react";
import { camelizeKeys } from "humps";
import { useUser as useAuth0User } from "@auth0/nextjs-auth0/client";
import axios from "axios";
import axiosRetry from "axios-retry";

import { apiRoutes } from "../../routes";
import errorReporter from "../../utils/errorReporter";
// import isMobile from "../../utils/isMobile";
import { useOnboardingInfo } from "../../hooks/api/user";
import { useUserContext } from "../../containers/withUserContext";
import LocalStorage from "../LocalStorage";
import { BAccessToken, TAccessToken, TLoginParameter } from "./types";

axiosRetry(axios, {
  retries: 2,
  retryDelay: (retryCount) => retryCount * 1000,
});

export const fetchAccessToken = async (): Promise<TAccessToken> => {
  return axios
    .get<BAccessToken>(apiRoutes.AUTH_ACCESS_TOKEN)
    .then((result) => camelizeKeys(result.data) as TAccessToken);
};

export const logout = () => {
  LocalStorage.deleteAuth();
  errorReporter.setUser(null);
  window.location.replace(apiRoutes.AUTH_LOGOUT);
};

export const useUser = (params?: { skipSessionCheck: boolean }) => {
  const auth0User = useAuth0User();
  const user = auth0User.user;
  const userId = LocalStorage.getUserId();
  const accessToken = LocalStorage.getAccessToken();

  const {
    reset: resetUserContext,
    hasIdentifiedByErrorReporter,
    identifyByErrorReporter,
    updateShouldShowLoggedOutOverlay,
  } = useUserContext();
  const { data: userOnboardingInfo, error } = useOnboardingInfo(
    userId ? { userId } : null
  );

  const isOnboardingLoading = userId ? !userOnboardingInfo && !error : false;
  const isLoading = auth0User.isLoading || isOnboardingLoading;

  const isLoggedIn =
    // Auth0 info succeeded.
    !!auth0User.user &&
    // User exists in Quira.
    userOnboardingInfo?.userId !== undefined;

  const _login = useCallback(
    (params?: TLoginParameter) => {
      resetUserContext();
      const searchParams = new URLSearchParams();

      searchParams.set("screen", params?.screen || "");
      searchParams.set("tcaccepted", String(!!params?.tcAccepted));

      if (params?.afterLoginPath) {
        searchParams.set("to", params.afterLoginPath);
      } else {
        const { pathname } = window.location;

        if (!pathname || pathname === "/") {
          const currentAfterLoginPath = new URL(
            window.location.href
          ).searchParams.get("to");

          if (currentAfterLoginPath) {
            searchParams.set("to", currentAfterLoginPath);
          }
        } else {
          searchParams.set("to", pathname);
        }
      }
      if (params?.prompt) {
        searchParams.set("prompt", params.prompt);
      }
      if (typeof params?.withOrgPermission === "boolean") {
        searchParams.set(
          "withOrgPermission",
          params.withOrgPermission.toString()
        );
      }
      const queryString = searchParams.toString();
      const path =
        apiRoutes.AUTH_LOGIN + (queryString ? `?${queryString}` : "");
      window.location.replace(path);
    },
    [resetUserContext]
  );

  const _logout = useCallback(() => {
    resetUserContext();
    updateShouldShowLoggedOutOverlay(true);
    logout();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetUserContext]);

  // identify user with error reporter
  useEffect(() => {
    if (!userOnboardingInfo || hasIdentifiedByErrorReporter) {
      return;
    }
    identifyByErrorReporter(true);
    errorReporter.setUser({
      id: userOnboardingInfo.userId.toString(),
      username: userOnboardingInfo.username,
    });
  }, [
    userOnboardingInfo,
    hasIdentifiedByErrorReporter,
    identifyByErrorReporter,
  ]);

  // Force logout user if session cookie and the user id & access token are not in-sync
  useEffect(() => {
    if (params?.skipSessionCheck) {
      return;
    }
    if (!userId && !accessToken) {
      return;
    }
    if (user && (!userId || !accessToken)) {
      _logout();
    }
  }, [_logout, user, userId, accessToken, params?.skipSessionCheck]);

  return {
    ...auth0User,
    isLoggedIn,
    userId,
    isLoading,
    logout: _logout,
    login: _login,
  };
};

export * from "./types";
